import { Button } from 'antd';
import { Plus, ShieldAlert, Wrench } from 'lucide-react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { DirectoryTable } from '../../../components/DirectoryTable';
import { useCanManageService } from '../../../hooks/use-can-manage-service';
import { useDirectoryDrawer } from '../../../hooks/use-directory-drawer';
import { buildPartTypeColumns } from '../components/build-part-type-columns';
import { PartTypeFormDrawer } from '../components/PartTypeFormDrawer';
import { usePartTypes } from '../hooks/use-part-types';
import type { PartTypeView } from '../part-types.api';
import { toPartTypeInput, type PartTypeFormValues } from '../policies/part-type-form.policy';

/** The catalogue the parts register groups by (gitaumoses4/chukua#174), with the expected life that makes a short-lived replacement show as early failure. */
export function PartTypesPage() {
  const { state, list, isRefreshing, saveError, saving, resetSaveError, create, update, deactivate, reactivate } = usePartTypes();
  const canManage = useCanManageService();
  const drawer = useDirectoryDrawer<PartTypeView>(resetSaveError);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  const actions = canManage ? { onEdit: drawer.openDrawer, onDeactivate: deactivate, onReactivate: reactivate } : undefined;
  const newButton = canManage && (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={() => drawer.openDrawer()}>
      New part type
    </Button>
  );
  const submit = (values: PartTypeFormValues) => (drawer.editing ? update(drawer.editing.id, toPartTypeInput(values)) : create(toPartTypeInput(values)));

  return (
    <div className="flex flex-col gap-4">
      {newButton && <div className="flex justify-end">{newButton}</div>}
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load part types" description="Something went wrong loading the catalogue. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading part types" withSearch />}
      {state.status === 'loaded' && (
        <DirectoryTable
          page={state.page}
          columns={buildPartTypeColumns({ list, actions })}
          list={list}
          isRefreshing={isRefreshing}
          newButton={newButton}
          emptyIcon={Wrench}
          emptyTitle="No part types yet"
          emptyDescription="Add the kinds of part you fit (brake pad, alternator, fuel filter) so work order parts lines can be grouped and compared."
          searchPlaceholder="Search part types"
          noMatchTitle="No matching part types"
        />
      )}
      <PartTypeFormDrawer open={drawer.open} editing={drawer.editing} error={saveError} submitting={saving} onSubmit={submit} onClose={drawer.close} />
    </div>
  );
}
